import { useFetch } from '@/composables/useFetch';
import type { Employee } from '@/types/api';
import type { KitchenPosition, ShiftAssignment, ShiftSlot } from '@/types/employee';
import { defineStore } from 'pinia';
import { computed, ref } from 'vue';
import { useEnvStore } from './envStore';
import { useUserStore } from './useUserStore';

export const useEmployeeStore = defineStore('employee', () => {
  const shiftsRaw = ref<
    {
      user: Employee;
      restaurant_user_id: number;
      shifts: Array<{
        id: number;
        date: string;
        slot: ShiftSlot;
        kitchen_position: KitchenPosition;
        start_time?: string; // HH:MM format
        end_time?: string; // HH:MM format
      }>;
    }[]
  >([]);
  const shiftSlots = ref<ShiftSlot[]>([]);
  const kitchenPositions = ref<KitchenPosition[]>([]);
  const kitchenPositionsByCategory = ref<Record<string, KitchenPosition[]>>({});
  const restaurantUsers = ref<Employee[]>([]);

  function setShifts(data: typeof shiftsRaw.value) {
    shiftsRaw.value = data;
  }
  function setShiftSlots(data: ShiftSlot[]) {
    shiftSlots.value = data;
  }
  function setKitchenPositions(data: KitchenPosition[] | Record<string, KitchenPosition[]>) {
    if (Array.isArray(data)) {
      // Legacy format - treat as kitchen category
      kitchenPositions.value = data;
      kitchenPositionsByCategory.value = { kitchen: data };
    } else {
      // New grouped format
      kitchenPositionsByCategory.value = data;
      // Flatten all positions for backward compatibility
      kitchenPositions.value = Object.values(data).flat();
    }
  }

  function setRestaurantUsers(data: Employee[] | { users: Employee[] }) {
    if (Array.isArray(data)) {
      restaurantUsers.value = data;
    } else if ('users' in data && Array.isArray(data.users)) {
      restaurantUsers.value = data.users;
    } else {
      restaurantUsers.value = [];
    }
  }

  async function fetchRestaurantUsersIfNeeded() {
    if (restaurantUsers.value.length > 0) return;
    const { fetchData } = useFetch();
    const apiUrl = useEnvStore().apiUrl;
    const userStore = useUserStore();
    // Try both possible restaurant id sources
    const restaurantId = userStore.user?.active_restaurant?.id || userStore.user?.restaurants?.[0]?.id;
    if (!restaurantId) return;
    const res = await fetchData(`${apiUrl}/manager/${restaurantId}/team`, 'GET');
    if (res.data) setRestaurantUsers(res.data);
  }

  async function fetchRestaurantUsers(force = false) {
    if (!force && restaurantUsers.value.length > 0) return;
    const { fetchData } = useFetch();
    const apiUrl = useEnvStore().apiUrl;
    const userStore = useUserStore();
    // Try both possible restaurant id sources
    const restaurantId = userStore.user?.active_restaurant?.id || userStore.user?.restaurants?.[0]?.id;
    if (!restaurantId) return;
    const res = await fetchData(`${apiUrl}/manager/${restaurantId}/team`, 'GET');
    if (res.data) setRestaurantUsers(res.data);
  }

  async function fetchShiftSlotsIfNeeded() {
    if (shiftSlots.value.length > 0) return;
    const { fetchData } = useFetch();
    const apiUrl = useEnvStore().apiUrl;
    const userStore = useUserStore();
    const currentRestaurantId = userStore.user?.restaurants[0]?.id;
    if (!currentRestaurantId) return;
    const res = await fetchData(`${apiUrl}/restaurants/${currentRestaurantId}/shift-slots`, 'GET');
    if (res.data) setShiftSlots(res.data);
  }

  async function fetchShiftsIfNeeded({
    restaurantId,
    startDate,
    endDate,
    forceRefetch = false, // Add forceRefetch parameter
  }: {
    restaurantId: string;
    startDate: string;
    endDate: string;
    forceRefetch?: boolean; // Define it in the type
  }) {
    if (!restaurantId || !startDate || !endDate) return;

    // Only check cache if not forcing refetch
    if (!forceRefetch && shiftsRaw.value.length > 0) {
      const firstShiftDate = shiftsRaw.value[0]?.shifts?.[0]?.date;
      // Consider if this caching logic is sufficient or needs to check the entire date range.
      if (firstShiftDate === startDate) {
        // console.log('Skipping fetch, data potentially cached for start date:', startDate);
        return;
      }
    }
    const { fetchData } = useFetch();
    const apiUrl = useEnvStore().apiUrl;
    // console.log('Fetching shifts for range:', startDate, 'to', endDate, 'Force refetch:', forceRefetch);
    const { data } = await fetchData(`${apiUrl}/planning/employees?restaurant_id=${restaurantId}&start_date=${startDate}&end_date=${endDate}`, 'GET');
    if (data) setShifts(data.data);
    // Optionally handle error
  }

  async function fetchKitchenPositionsIfNeeded() {
    if (kitchenPositions.value.length > 0) return;
    const { fetchData } = useFetch();
    const apiUrl = useEnvStore().apiUrl;
    const userStore = useUserStore();
    const currentRestaurantId = userStore.user?.restaurants[0]?.id;
    if (!currentRestaurantId) return;
    const res = await fetchData(`${apiUrl}/restaurants/${currentRestaurantId}/kitchen-positions`, 'GET');
    if (res.data.data) setKitchenPositions(res.data.data);
    else if (res.data) setKitchenPositions(res.data);
  }

  async function removeRestaurantUser(restaurantId: string, userId: string) {
    const { fetchData } = useFetch();
    const apiUrl = useEnvStore().apiUrl;
    const res = await fetchData(`${apiUrl}/manager/${restaurantId}/team/${userId}`, 'DELETE');
    if (res.data) {
      // remove user locally
      restaurantUsers.value = restaurantUsers.value.filter((u) => u.id !== userId);
    }
    return res;
  }

  const usersByKitchenPositionAndCategory = computed(() => {
    const result: Record<string, Record<number, { position: KitchenPosition; users: Map<string, Employee> }>> = {};

    // Initialize categories
    for (const [department, positions] of Object.entries(kitchenPositionsByCategory.value)) {
      result[department] = {};
      for (const position of positions) {
        result[department][position.id] = { position, users: new Map() };
      }
    }

    // Track all users assigned to positions this week
    const assignedUserIds = new Set<string>();

    // Loop through all shifts and group by category
    for (const employee of shiftsRaw.value) {
      for (const shift of employee.shifts) {
        const kp = shift.kitchen_position;
        if (kp && kp.department) {
          const departmentName = kp.department.name;
          if (!result[departmentName]) {
            result[departmentName] = {};
          }
          if (!result[departmentName][kp.id]) {
            result[departmentName][kp.id] = { position: kp, users: new Map() };
          }
          result[departmentName][kp.id].users.set(employee.user.id, employee.user);
        }
        assignedUserIds.add(employee.user.id);
      }
    }

    // Find users not assigned to any position this week
    const unassignedUsers = restaurantUsers.value.filter((user) => !assignedUserIds.has(user.id));

    // Add unassigned users to ALL categories
    if (unassignedUsers.length > 0) {
      for (const department of Object.keys(result)) {
        result[department][-1] = {
          position: {
            id: -1,
            name: 'noPosition',
            color: 'cccccc',
            department: { id: -1, name: department },
          } as KitchenPosition,
          users: new Map(unassignedUsers.map((u) => [u.id, u])),
        };
      }
    }

    // Convert to final format and ensure "No position" appears first
    const finalResult: Record<string, Array<{ position: KitchenPosition; users: Employee[] }>> = {};
    for (const [department, positions] of Object.entries(result)) {
      const positionsArray = Object.values(positions).map(({ position, users }) => ({
        position,
        users: Array.from(users.values()),
      }));

      // Sort to put "No position" (-1 id) first, then regular positions
      positionsArray.sort((a, b) => {
        if (a.position.id === -1) return -1;
        if (b.position.id === -1) return 1;
        return a.position.id - b.position.id;
      });

      finalResult[department] = positionsArray;
    }

    return finalResult;
  });

  const shiftsByDayAndSlot = computed(() => {
    const result: Record<string, Record<string, ShiftAssignment[]>> = {};
    for (const employee of shiftsRaw.value) {
      for (const shift of employee.shifts) {
        const date = shift.date;
        const slotName = shift.slot.name;
        if (!result[date]) result[date] = {};
        if (!result[date][slotName]) result[date][slotName] = [];
        result[date][slotName].push({
          id: shift.id,
          user: employee.user,
          kitchen_position: shift.kitchen_position,
          restaurant_user_id: employee.restaurant_user_id,
          start_time: shift.start_time,
          end_time: shift.end_time,
        });
      }
    }
    return result;
  });

  const positionCategories = computed(() => Object.keys(kitchenPositionsByCategory.value));

  return {
    shiftsRaw,
    shiftSlots,
    kitchenPositions,
    kitchenPositionsByCategory,
    restaurantUsers,
    setShifts,
    setShiftSlots,
    setKitchenPositions,
    shiftsByDayAndSlot,
    usersByKitchenPositionAndCategory,
    positionCategories,
    setRestaurantUsers,
    fetchShiftSlotsIfNeeded,
    fetchKitchenPositionsIfNeeded,
    fetchRestaurantUsersIfNeeded,
    fetchRestaurantUsers,
    fetchShiftsIfNeeded,
    removeRestaurantUser,
  };
});
